// TanStack Query hooks for object_types + property_types CRUD.
// Atomic saves go through the save_object_type RPC (SECURITY DEFINER) per plan §3.1.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type {
  ObjectType,
  PropertyType,
  ObjectTypeWithProperties,
  SaveObjectTypeInput,
} from "@/types/ontology";
import {
  StaleVersionError,
  ActiveMappingsError,
  InvalidApiNameError,
  DataConstraintViolationError,
} from "@/types/ontology";

// ── Query keys (plan §1.3) ────────────────────────────────────────────────────

export const OBJECT_TYPES_KEY = ["object-types"] as const;

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Map a Supabase RPC error to a typed error when applicable. */
function mapRpcError(error: { message?: string; code?: string } | null): Error {
  if (!error) return new Error("Unknown error");
  // stale_version: P0001
  if (error.message?.includes("stale_version") || error.code === "P0001") {
    return new StaleVersionError();
  }
  // active mappings block delete: P0004
  if (error.message?.includes("active mappings") || error.code === "P0004") {
    return new ActiveMappingsError();
  }
  // existing data violates a newly-enabled required/unique flag: P0005
  if (
    error.code === "P0005" ||
    error.message?.includes("required_violation:") ||
    error.message?.includes("unique_violation:")
  ) {
    return new DataConstraintViolationError(error.message ?? undefined);
  }
  // invalid api_name convention: 22023
  if (error.message?.includes("api_name") || error.code === "22023") {
    return new InvalidApiNameError(error.message ?? "api_name violates naming convention");
  }
  return new Error(error.message ?? "Unknown error");
}

// ── Query ─────────────────────────────────────────────────────────────────────

/** List all non-deleted object types. */
export function useObjectTypes() {
  const query = useQuery({
    queryKey: OBJECT_TYPES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("object_types")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: true });

      if (error) throw new Error(error.message);

      return (data as ObjectType[]) ?? [];
    },
  });

  return { objectTypes: query.data ?? [], isLoading: query.isLoading, error: query.error };
}

/** Load one object type with its property types eager-loaded. */
export function useObjectType(id: string | null) {
  const query = useQuery({
    queryKey: [...OBJECT_TYPES_KEY, id],
    enabled: id !== null,
    queryFn: async () => {
      if (!id) return null;

      const [typeResult, propsResult] = await Promise.all([
        supabase.from("object_types").select("*").eq("id", id).single(),
        supabase
          .from("property_types")
          .select("*")
          .eq("object_type_id", id)
          .order("position", { ascending: true }),
      ]);

      if (typeResult.error) throw new Error(typeResult.error.message);
      if (propsResult.error) throw new Error(propsResult.error.message);

      return {
        ...(typeResult.data as ObjectType),
        property_types: (propsResult.data as PropertyType[]) ?? [],
      } as ObjectTypeWithProperties;
    },
  });

  return { objectType: query.data ?? null, isLoading: query.isLoading, error: query.error };
}

// ── Mutations ─────────────────────────────────────────────────────────────────

interface SaveObjectTypeArgs {
  id: string | null;
  expectedUpdatedAt: string | null; // null for new object types
  input: SaveObjectTypeInput;
}

export function useSaveObjectType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, expectedUpdatedAt, input }: SaveObjectTypeArgs) => {
      const { data, error } = await supabase.rpc("save_object_type", {
        p_id: id,
        p_expected_updated_at: expectedUpdatedAt,
        p_input: input as unknown as Record<string, unknown>,
      });

      if (error) throw mapRpcError(error);

      return data as ObjectType;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: OBJECT_TYPES_KEY });
    },
    onError: (err) => {
      console.error("hook:useSaveObjectType", err);
      // StaleVersionError (StaleVersionDialog) and DataConstraintViolationError
      // (friendly inline message + toast in ObjectTypeEditor) are handled by the
      // caller; don't double-toast for those.
      if (!(err instanceof StaleVersionError) && !(err instanceof DataConstraintViolationError)) {
        toast({
          title: "Failed to save object type",
          description: (err as Error).message,
          variant: "destructive",
        });
      }
    },
  });
}

export function useSoftDeleteObjectType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("soft_delete_object_type", { p_id: id });

      if (error) throw mapRpcError(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: OBJECT_TYPES_KEY });
    },
    onError: (err) => {
      console.error("hook:useSoftDeleteObjectType", err);
      // ActiveMappingsError is surfaced inline by the caller; don't toast for it.
      if (!(err instanceof ActiveMappingsError)) {
        toast({
          title: "Failed to delete object type",
          description: (err as Error).message,
          variant: "destructive",
        });
      }
    },
  });
}
